/**
 * Yamiward — The Ward Arcade
 * Two machines: ONI RUNNER (canvas endless runner) and KANJI MATCH (memory).
 * Scores post to the community backend; leaderboards are shared by everyone.
 * All user-supplied strings (handles) are rendered with textContent — never innerHTML.
 */

document.addEventListener("DOMContentLoaded", () => {
    const BOARD_API = "https://api.yamiward.com";

    // Same anonymous identity the Ward Board uses (app.js creates it first,
    // but guard-create in case script order ever changes).
    // Scores are submitted under the Board-issued signed identity (identity.js).

    const handleInput = document.getElementById("arcade-handle");
    if (handleInput) {
        handleInput.value = (window.YW_STORE && YW_STORE.get("yw_handle")) || "";
        handleInput.addEventListener("change", () => {
            let h = handleInput.value.trim();
            if (h && !h.startsWith("@")) h = "@" + h;
            handleInput.value = h;
            if (h) window.YW_STORE && YW_STORE.set("yw_handle", h);
        });
    }

    function getHandle() {
        let h = (handleInput && handleInput.value.trim()) || (window.YW_STORE && YW_STORE.get("yw_handle")) || "@realm_wanderer";
        if (!h.startsWith("@")) h = "@" + h;
        return h;
    }

    function toast(message) {
        let t = document.querySelector(".toast-notification");
        if (!t) {
            t = document.createElement("div");
            t.classList.add("toast-notification");
            t.style.cssText = "position:fixed;bottom:30px;left:50%;transform:translateX(-50%);background:#E51E25;color:#fff;padding:12px 24px;font-family:'Space Mono',monospace;font-size:0.75rem;letter-spacing:0.1em;font-weight:700;z-index:3000;box-shadow:0 10px 25px rgba(229,30,37,0.4);transition:opacity .3s ease,transform .3s ease;";
            document.body.appendChild(t);
        }
        t.textContent = message;
        t.style.opacity = "1";
        t.style.transform = "translateX(-50%) translateY(0)";
        setTimeout(() => {
            t.style.opacity = "0";
            t.style.transform = "translateX(-50%) translateY(10px)";
        }, 3000);
    }

    // ---- scores + leaderboards ----------------------------------------------

    async function loadBoard(game) {
        const ol = document.getElementById("lb-" + game);
        if (!ol) return;
        try {
            const res = await fetch(`${BOARD_API}/api/leaderboard/${game}`);
            const rows = await res.json();
            ol.innerHTML = "";
            if (!Array.isArray(rows) || rows.length === 0) {
                const li = document.createElement("li");
                li.className = "lb-empty";
                li.textContent = "NO SCORES YET — THE BOARD AWAITS.";
                ol.appendChild(li);
                return;
            }
            const medals = ["🥇", "🥈", "🥉"];
            rows.forEach((r, i) => {
                const li = document.createElement("li");
                const rank = document.createElement("span");
                rank.className = "lb-rank";
                rank.textContent = medals[i] || String(i + 1).padStart(2, "0");
                const who = document.createElement("span");
                who.className = "lb-handle";
                who.textContent = r.handle;
                const pts = document.createElement("span");
                pts.className = "lb-score";
                pts.textContent = Number(r.best).toLocaleString();
                li.appendChild(rank); li.appendChild(who); li.appendChild(pts);
                ol.appendChild(li);
            });
        } catch {
            ol.innerHTML = "";
            const li = document.createElement("li");
            li.className = "lb-empty";
            li.textContent = "LEADERBOARD UNREACHABLE — REFRESH TO RETRY.";
            ol.appendChild(li);
        }
    }

    // Submit only when the run beats this browser's previous best — the server
    // keeps MAX(score) per player anyway, so lesser runs are just wasted quota.
    async function submitScore(game, score) {
        const bestKey = "yw_best_" + game;
        const prevBest = Number((window.YW_STORE && YW_STORE.get(bestKey)) || 0);
        if (score <= 0) return;
        if (score <= prevBest) return;
        window.YW_STORE && YW_STORE.set(bestKey, String(score));
        try {
            const res = await YW_ID.post(`${BOARD_API}/api/scores`, { game, handle: getHandle(), score });
            if (res.ok) {
                toast("NEW BEST — SCORE ETCHED INTO THE BOARD");
                loadBoard(game);
            } else {
                const out = await res.json().catch(() => ({}));
                toast((out.error || "SCORE REJECTED").toUpperCase());
            }
        } catch {
            toast("NETWORK ERROR — SCORE NOT SAVED");
        }
    }

    // =========================================================================
    // GAME 1 — ONI RUNNER  (endless runner: jump the torii gates and stones)
    // =========================================================================
    (function oniRunner() {
        const canvas = document.getElementById("oni-canvas");
        const overlay = document.getElementById("oni-overlay");
        const scoreEl = document.getElementById("oni-score");
        const bestEl = document.getElementById("oni-best");
        if (!canvas || !overlay) return;
        const ctx = canvas.getContext("2d");

        const W = 640, H = 240, GROUND = 198;
        const GRAVITY = 2000, JUMP_V = -700;

        let state = "idle"; // idle | running | dead
        let player, obstacles, distance, score, speed, spawnIn, lastTs, raf;

        function best() { return Number((window.YW_STORE && YW_STORE.get("yw_best_oni-runner")) || 0); }
        function drawBest() { if (bestEl) bestEl.textContent = "BEST " + best().toLocaleString(); }
        drawBest();

        function reset() {
            player = { x: 70, y: GROUND, w: 30, h: 32, vy: 0, onGround: true };
            obstacles = [];
            distance = 0; score = 0;
            speed = 265;
            spawnIn = 1.1;
            lastTs = 0;
        }

        function setOverlay(html) { overlay.innerHTML = html; overlay.style.display = "flex"; }

        function showIdle() {
            state = "idle";
            setOverlay(`<div class="ov-title">鬼走り — ONI RUNNER</div>
                <div class="ov-sub">JUMP THE GATES. OUTRUN THE WARD.</div>
                <button type="button" class="btn btn-primary" id="oni-start">INSERT SOUL — START</button>
                <div class="ov-hint">TAP / CLICK / SPACE TO JUMP</div>`);
            document.getElementById("oni-start").addEventListener("click", start);
            drawFrame(); // static backdrop behind the overlay
        }

        function showDead() {
            state = "dead";
            setOverlay(`<div class="ov-title">CAUGHT BY THE WARD</div>
                <div class="ov-sub">SCORE ${score.toLocaleString()}</div>
                <button type="button" class="btn btn-primary" id="oni-again">RUN AGAIN</button>`);
            document.getElementById("oni-again").addEventListener("click", start);
            submitScore("oni-runner", score);
            drawBest();
        }

        function start() {
            reset();
            overlay.style.display = "none";
            state = "running";
            lastTs = performance.now();
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(loop);
        }

        function jump() {
            if (state !== "running") return;
            if (player.onGround) {
                player.vy = JUMP_V;
                player.onGround = false;
            }
        }

        canvas.addEventListener("pointerdown", (e) => { e.preventDefault(); jump(); });
        window.addEventListener("keydown", (e) => {
            if (state !== "running") return;
            if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); }
        });

        function spawn() {
            const roll = Math.random();
            let o;
            if (roll < 0.45) o = { type: "stone", w: 22, h: 26 };
            else if (roll < 0.8) o = { type: "torii", w: 34, h: 46 };
            else o = { type: "stones2", w: 52, h: 24 };
            o.x = W + 10;
            obstacles.push(o);
        }

        function loop(ts) {
            if (state !== "running") return;
            let dt = (ts - lastTs) / 1000;
            lastTs = ts;
            if (dt > 0.05) dt = 0.05; // tab-switch clamp

            // physics
            player.vy += GRAVITY * dt;
            player.y += player.vy * dt;
            if (player.y >= GROUND) { player.y = GROUND; player.vy = 0; player.onGround = true; }

            // world
            speed += 7 * dt;
            distance += speed * dt;
            score = Math.floor(distance / 12);

            spawnIn -= dt;
            if (spawnIn <= 0) {
                spawn();
                // gap shrinks as speed grows, but always clearable
                spawnIn = (0.85 + Math.random() * 0.75) * (320 / speed) + 0.35;
            }
            obstacles.forEach(o => o.x -= speed * dt);
            obstacles = obstacles.filter(o => o.x + o.w > -20);

            // collision (with a little forgiveness)
            const px = player.x + 5, pw = player.w - 10;
            const py = player.y - player.h + 4, ph = player.h - 6;
            for (const o of obstacles) {
                const ox = o.x + 3, ow = o.w - 6;
                const oy = GROUND - o.h + 3, oh = o.h - 3;
                if (px < ox + ow && px + pw > ox && py < oy + oh && py + ph > oy) {
                    drawFrame();
                    showDead();
                    return;
                }
            }

            drawFrame();
            if (scoreEl) scoreEl.textContent = "SCORE " + score.toLocaleString();
            raf = requestAnimationFrame(loop);
        }

        function drawFrame() {
            // night sky
            ctx.fillStyle = "#0a0a0d";
            ctx.fillRect(0, 0, W, H);
            // crimson moon
            ctx.beginPath();
            ctx.arc(540, 52, 26, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(229,30,37,0.85)";
            ctx.fill();
            ctx.beginPath();
            ctx.arc(540, 52, 34, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(229,30,37,0.25)";
            ctx.lineWidth = 2;
            ctx.stroke();
            // distant skyline dashes (parallax off distance)
            ctx.fillStyle = "#141418";
            const off = (distance * 0.25) % 80;
            for (let i = -1; i < 10; i++) {
                const bx = i * 80 - off;
                ctx.fillRect(bx, 128 + (i % 3) * 10, 34, GROUND - 128 - (i % 3) * 10);
            }
            // ground
            ctx.strokeStyle = "#2a2a2e";
            ctx.beginPath();
            ctx.moveTo(0, GROUND + 1);
            ctx.lineTo(W, GROUND + 1);
            ctx.stroke();
            ctx.fillStyle = "#0e0e11";
            ctx.fillRect(0, GROUND + 2, W, H - GROUND);
            // ground ticks
            ctx.fillStyle = "#1d1d20";
            const toff = (distance * 1) % 40;
            for (let i = 0; i < 18; i++) ctx.fillRect(i * 40 - toff, GROUND + 12, 18, 3);

            // obstacles
            for (const o of (obstacles || [])) drawObstacle(o);

            // player — the oni
            if (player) {
                ctx.font = "30px serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "alphabetic";
                ctx.fillText("👺", player.x + player.w / 2, player.y);
            }
        }

        function drawObstacle(o) {
            const baseY = GROUND;
            if (o.type === "torii") {
                ctx.fillStyle = "#E51E25";
                const px = o.x, h = o.h, w = o.w;
                ctx.fillRect(px + 3, baseY - h + 8, 5, h - 8);          // left post
                ctx.fillRect(px + w - 8, baseY - h + 8, 5, h - 8);      // right post
                ctx.fillRect(px - 2, baseY - h + 2, w + 4, 6);          // top beam
                ctx.fillRect(px + 1, baseY - h + 14, w - 2, 4);         // lower beam
            } else if (o.type === "stone") {
                ctx.fillStyle = "#3a3a40";
                ctx.fillRect(o.x, baseY - o.h, o.w, o.h);
                ctx.fillStyle = "#0a0a0d";
                ctx.font = "13px serif";
                ctx.textAlign = "center";
                ctx.fillText("怨", o.x + o.w / 2, baseY - o.h / 2 + 5);
            } else { // stones2 — low double slab
                ctx.fillStyle = "#3a3a40";
                ctx.fillRect(o.x, baseY - o.h, o.w / 2 - 3, o.h);
                ctx.fillRect(o.x + o.w / 2 + 3, baseY - o.h + 6, o.w / 2 - 3, o.h - 6);
            }
        }

        showIdle();
    })();

    // =========================================================================
    // GAME 2 — KANJI MATCH  (memory pairs: faster + fewer moves = higher score)
    // =========================================================================
    (function kanjiMatch() {
        const grid = document.getElementById("kanji-grid");
        const movesEl = document.getElementById("kanji-moves");
        const timeEl = document.getElementById("kanji-time");
        const bestEl = document.getElementById("kanji-best");
        const resetBtn = document.getElementById("kanji-reset");
        const resultEl = document.getElementById("kanji-result");
        if (!grid) return;

        const KANJI = ["闇", "鬼", "影", "幽", "霊", "面", "街", "門"];

        let first, lock, moves, matched, seconds, timer, started;

        function best() { return Number((window.YW_STORE && YW_STORE.get("yw_best_kanji-match")) || 0); }
        function drawBest() { if (bestEl) bestEl.textContent = "BEST " + best().toLocaleString(); }

        function fmtTime(s) {
            return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
        }

        function setup() {
            clearInterval(timer);
            first = null; lock = false; moves = 0; matched = 0; seconds = 0; started = false;
            if (movesEl) movesEl.textContent = "MOVES 0";
            if (timeEl) timeEl.textContent = "0:00";
            if (resultEl) resultEl.style.display = "none";
            drawBest();

            const deck = KANJI.concat(KANJI)
                .map(k => ({ k, r: Math.random() }))
                .sort((a, b) => a.r - b.r)
                .map(x => x.k);

            grid.innerHTML = "";
            deck.forEach((k) => {
                const card = document.createElement("button");
                card.type = "button";
                card.className = "kanji-card";
                card.setAttribute("aria-label", "hidden card");
                const face = document.createElement("span");
                face.className = "kanji-face";
                face.textContent = k;
                const back = document.createElement("span");
                back.className = "kanji-back";
                back.textContent = "◈";
                card.appendChild(back);
                card.appendChild(face);
                card.addEventListener("click", () => flip(card, k));
                grid.appendChild(card);
            });
        }

        function startClock() {
            if (started) return;
            started = true;
            timer = setInterval(() => {
                seconds++;
                if (timeEl) timeEl.textContent = fmtTime(seconds);
            }, 1000);
        }

        function flip(card, k) {
            if (lock || card.classList.contains("revealed") || card.classList.contains("matched")) return;
            startClock();
            card.classList.add("revealed");

            if (!first) {
                first = { card, k };
                return;
            }

            moves++;
            if (movesEl) movesEl.textContent = "MOVES " + moves;

            if (first.k === k) {
                card.classList.add("matched");
                first.card.classList.add("matched");
                first = null;
                matched++;
                if (matched === KANJI.length) win();
            } else {
                lock = true;
                const a = first.card;
                first = null;
                setTimeout(() => {
                    a.classList.remove("revealed");
                    card.classList.remove("revealed");
                    lock = false;
                }, 700);
            }
        }

        function win() {
            clearInterval(timer);
            const score = Math.max(0, 10000 - Math.max(0, moves - 8) * 300 - seconds * 15);
            if (resultEl) {
                resultEl.innerHTML = "";
                const t = document.createElement("div");
                t.className = "ov-title";
                t.textContent = "ALL SPIRITS PAIRED";
                const s = document.createElement("div");
                s.className = "ov-sub";
                s.textContent = `SCORE ${score.toLocaleString()} — ${moves} MOVES · ${fmtTime(seconds)}`;
                resultEl.appendChild(t);
                resultEl.appendChild(s);
                resultEl.style.display = "block";
            }
            submitScore("kanji-match", score);
            drawBest();
        }

        if (resetBtn) resetBtn.addEventListener("click", setup);
        setup();
    })();

    // initial leaderboards
    loadBoard("oni-runner");
    loadBoard("kanji-match");
});
